'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useI18n } from '@/lib/i18n';
import BlobImage from '@/components/BlobImage';
import { compressImage } from '@/lib/imageCompress';
import { ThinSheepStrip } from '@/components/redesign/sheep-strip';
import { ZineHeader } from '@/components/ui/ZineHeader';
import { splitZineTitle } from '@/lib/hooks';
import { Card } from '@/components/ui/Card';

// 2026-05-09 build 1173 — B1 NEWSPRINT ZINE port of the Profile (You)
// page, per Veronika's Claude Design ProfilePage.jsx. Visual-only port:
// state, persistence (knitter-name + knitter-self-message in
// localStorage), and the existing field labels are unchanged. Avatar
// "EDIT" badge and the cancel "×" button from the design are skipped
// since they're new functionality without backing data.

const fontDisplay = "var(--font-geist-sans), system-ui, -apple-system, sans-serif";
const fontSerif = "'Fraunces', 'Times New Roman', serif";

const HALFTONE = {
  backgroundImage:
    'radial-gradient(circle, rgba(15,14,12,0.55) 1px, transparent 1.4px)',
  backgroundSize: '5px 5px',
};

// Cap stays at 68 chars per build 1147 — Veronika's iPhone Pro Max
// rendering hit the ellipsis-overflow guard at 70 with Czech text.
const SELF_MESSAGE_MAX = 68;

// 2026-05-09 build 1177 — Veronika replaced the placeholder sheep
// glyph from the design with the right-facing meadow sheep from
// `sheep-strip.tsx` (mirrored via scaleX(-1) so the head sticks out
// to the RIGHT, matching her hand-drawn avatar reference and the
// app icon). Same crisp-edges pixel-art silhouette as the home page
// strip — keeps the avatar visually consistent with the app's
// "sheep on halftone cream" identity.
function PixelSheep({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={Math.round((size * 32) / 20)}
      height={size}
      viewBox="0 0 32 20"
      style={{
        shapeRendering: 'crispEdges',
        display: 'block',
        transform: 'scaleX(-1)',
      }}
      aria-hidden
    >
      {/* Body — cloud-like silhouette */}
      <rect x="8" y="3" width="20" height="9" fill="currentColor" />
      <rect x="9" y="1" width="4" height="2" fill="currentColor" />
      <rect x="14" y="0" width="4" height="3" fill="currentColor" />
      <rect x="19" y="1" width="4" height="2" fill="currentColor" />
      <rect x="24" y="2" width="3" height="1" fill="currentColor" />
      {/* Head — sticks out one side; scaleX(-1) flips to the right */}
      <rect x="2" y="6" width="6" height="6" fill="currentColor" />
      <rect x="6" y="4" width="2" height="2" fill="currentColor" />
      {/* Eye — cream dot on the head */}
      <rect x="4" y="7" width="1" height="1" fill="var(--paper-1)" />
      {/* Legs */}
      <rect x="9" y="12" width="2" height="6" fill="currentColor" />
      <rect x="13" y="12" width="2" height="6" fill="currentColor" />
      <rect x="20" y="12" width="2" height="6" fill="currentColor" />
      <rect x="24" y="12" width="2" height="6" fill="currentColor" />
      {/* Tail (now on the rear-left after the flip) */}
      <rect x="27" y="4" width="1" height="2" fill="currentColor" />
    </svg>
  );
}

export default function ProfilePage() {
  const router = useRouter();
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [selfMessage, setSelfMessage] = useState('');
  const [saved, setSaved] = useState(false);
  // 2026-05-10 build 1260 — avatar photo. Stored as a `blob:sha256-…`
  // ref in localStorage, same per-device pattern as `knitter-name`
  // and `knitter-self-message`. The blob bytes themselves live in the
  // shared blob store (`compressImage` → `BlobImage`). Per-device by
  // design — matches how name/self-message are kept locally.
  const [photo, setPhoto] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setName(localStorage.getItem('knitter-name') || '');
    setSelfMessage(localStorage.getItem('knitter-self-message') || '');
    setPhoto(localStorage.getItem('knitter-photo') || null);
  }, []);

  const handlePhotoPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setPhotoUploading(true);
      // Avatar tile is 64×64 logical (≈128px @2x) — 256 caps gives
      // headroom for retina without bloating storage.
      const ref = await compressImage(file, { maxWidth: 256, maxHeight: 256, quality: 0.85 });
      localStorage.setItem('knitter-photo', ref);
      setPhoto(ref);
    } catch {
      // Silent fail — keep existing photo if any.
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSave = () => {
    localStorage.setItem('knitter-name', name.trim());
    localStorage.setItem('knitter-self-message', selfMessage.trim());
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const remaining = SELF_MESSAGE_MAX - selfMessage.length;
  const { head: titleHead, tail: titleTail } = splitZineTitle(t.profileTitle);

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--paper-1)',
        color: 'var(--ink)',
        fontFamily: fontSerif,
        position: 'relative',
      }}
    >
      {/* B1 zine header — 2-line title treatment if the i18n string
          contains a space, otherwise just the head in display caps.
          (`t.profileTitle` = 'Profile' / 'Profil' — single word, so
          the tail is empty and the page renders one display line.) */}
      <ZineHeader
        head={titleHead}
        tail={titleTail}
        titleStyle={{ lineHeight: 1 }}
        onBack={() => router.back()}
      />

      {/* Body */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          paddingBottom:
            'calc(var(--bottom-nav-height, 64px) + env(safe-area-inset-bottom) + 50px)',
        }}
      >
        {/* Avatar plate — photo (if uploaded) or sheep glyph + current
            name. Edit-pencil badge sits on the bottom-right corner of
            the tile and triggers a hidden file picker (2026-05-10
            build 1260). */}
        <div
          style={{
            padding: '20px 18px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            borderBottom: '1px solid var(--ink)',
          }}
        >
          <Card
            style={{
              width: 64,
              height: 64,
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {photo ? (
              <BlobImage
                src={photo}
                alt={name || t.profileKnitterFallback}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <>
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    ...HALFTONE,
                    opacity: 0.5,
                    mixBlendMode: 'multiply',
                  }}
                />
                <div style={{ position: 'relative', color: 'var(--ink)' }}>
                  <PixelSheep size={14} />
                </div>
              </>
            )}
            {/* Edit-pencil overlay */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={photoUploading}
              aria-label="Edit avatar"
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: 22,
                height: 22,
                background: 'var(--ink)',
                color: 'var(--paper-1)',
                border: '1px solid var(--ink)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: photoUploading ? 'wait' : 'pointer',
                padding: 0,
                opacity: photoUploading ? 0.6 : 1,
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoPick}
              style={{ display: 'none' }}
            />
          </Card>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: fontDisplay,
                fontWeight: 800,
                fontSize: 24,
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
                lineHeight: 0.95,
                color: 'var(--ink)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name || t.profileKnitterFallback}
            </div>
          </div>
        </div>

        {/* Form fields — labels kept verbatim from the previous
            profile page so this stays a visual-only port. */}
        <div
          style={{
            padding: '24px 14px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Name field */}
          <div>
            <label
              htmlFor="knitter-name-input"
              style={{
                display: 'block',
                fontFamily: fontDisplay,
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--ink)',
                marginBottom: 5,
              }}
            >
              {t.profileNameLabel}
            </label>
            <Card
              style={{
                padding: '10px 12px',
              }}
            >
              <input
                id="knitter-name-input"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t.profileNamePlaceholder}
                style={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontFamily: fontDisplay,
                  fontWeight: 800,
                  fontSize: 14,
                  letterSpacing: '-0.005em',
                  textTransform: 'uppercase',
                  lineHeight: 1.1,
                  color: 'var(--ink)',
                  padding: 0,
                }}
              />
            </Card>
            <div
              style={{
                fontFamily: fontSerif,
                fontStyle: 'italic',
                fontSize: 11,
                color: 'var(--ink-2)',
                marginTop: 4,
                lineHeight: 1.35,
              }}
            >
              {t.profileNameHint}
            </div>
          </div>

          {/* Self-message field */}
          <div>
            <label
              htmlFor="knitter-self-message-input"
              style={{
                display: 'block',
                fontFamily: fontDisplay,
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--ink)',
                marginBottom: 5,
              }}
            >
              {t.profileSelfMessageLabel}
            </label>
            <Card
              style={{
                padding: '10px 12px',
              }}
            >
              <input
                id="knitter-self-message-input"
                type="text"
                value={selfMessage}
                onChange={e => setSelfMessage(e.target.value.slice(0, SELF_MESSAGE_MAX))}
                maxLength={SELF_MESSAGE_MAX}
                placeholder={t.profileSelfMessagePlaceholder}
                style={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontFamily: fontDisplay,
                  fontWeight: 800,
                  fontSize: 14,
                  letterSpacing: '-0.005em',
                  textTransform: 'uppercase',
                  lineHeight: 1.1,
                  color: 'var(--ink)',
                  padding: 0,
                }}
              />
            </Card>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 8,
                marginTop: 4,
              }}
            >
              <p
                style={{
                  fontFamily: fontSerif,
                  fontStyle: 'italic',
                  fontSize: 11,
                  color: 'var(--ink-2)',
                  margin: 0,
                  lineHeight: 1.35,
                  flex: 1,
                }}
              >
                {t.profileSelfMessageHint}
              </p>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  letterSpacing: '0.12em',
                  color: remaining < 0 ? 'var(--over)' : 'var(--ink-3)',
                  fontVariantNumeric: 'tabular-nums',
                  flexShrink: 0,
                }}
              >
                {selfMessage.length} / {SELF_MESSAGE_MAX}
              </span>
            </div>
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            style={{
              width: '100%',
              height: 50,
              background: 'var(--ink)',
              color: 'var(--paper-1)',
              border: '1px solid var(--ink)',
              fontFamily: fontDisplay,
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              marginTop: 4,
            }}
          >
            {saved ? t.saved : t.save}
          </button>
        </div>
      </div>

      {/* Thin sheep strip — matches home + counter (Veronika
          2026-05-11). Sits directly on top of the BottomNav. */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'var(--bottom-nav-height, 64px)',
          height: 26,
          borderTop: '2px solid var(--ink)',
          background: 'var(--paper-1)',
          overflow: 'hidden',
          zIndex: 30,
        }}
      >
        <ThinSheepStrip />
      </div>
    </div>
  );
}
